import React, { Component } from 'react';
import { arrayOf, bool, object, func, shape, string } from 'prop-types';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { Switch, Route, withRouter } from 'react-router-dom';

import { useRouteConfiguration } from '../context/routeConfigurationContext';
import { propTypes } from '../util/types';
import * as log from '../util/log';
import { canonicalRoutePath } from '../util/routes';
import { useConfiguration } from '../context/configurationContext';

import { locationChanged } from '../ducks/routing.duck';
import { findHashTargetElement, isSamePathAndSearch } from '../util/samePageNavigation';

import { NamedRedirect } from '../components';
import NotFoundPage from '../containers/NotFoundPage/NotFoundPage';

import LoadableComponentErrorBoundary from './LoadableComponentErrorBoundary/LoadableComponentErrorBoundary';
import { captureAffiliateAttributionFromLocation } from '../util/affiliateAttribution';

const canShowComponent = props => {
  const { isAuthenticated, route } = props;
  const { auth } = route;
  return !auth || isAuthenticated;
};

const callLoadData = props => {
  const { match, location, route, dispatch, logoutInProgress, config } = props;
  const { loadData, name } = route;
  const shouldLoadData =
    typeof loadData === 'function' && canShowComponent(props) && !logoutInProgress;

  if (shouldLoadData) {
    dispatch(loadData(match.params, location.search, config))
      .then(() => {
        if (props.logLoadDataCalls) {
          // This gives good input for debugging issues on live environments, but with test it's not needed.
          console.log(`loadData success for ${name} route`);
        }
      })
      .catch(e => {
        log.error(e, 'load-data-failed', { routeName: name });
      });
  }
};

const setPageScrollPosition = (location, delayed, { samePage, preserveScroll } = {}) => {
  // Same-path hash / pushState (Seatics quantity drawer, in-page anchors):
  // never scroll. scrollIntoView remounts Seatics chrome and flashes the
  // quantity drawer shut on the first Select Tickets tap when Seatics sets a
  // real hash that matches an on-page id (#sea-mp, #list-ctn, etc.).
  // Map viewport searches still load new results, but must not move the page.
  if (samePage || preserveScroll) {
    return delayed;
  }

  if (!location.hash) {
    window.scroll({
      top: 0,
      left: 0,
    });
    return delayed;
  }

  const el = findHashTargetElement(location.hash);
  if (el) {
    el.scrollIntoView({
      block: 'start',
      behavior: 'smooth',
    });
    return delayed;
  }

  // Cross-page navigation: retry once after loadData / code-split.
  return window.setTimeout(() => {
    const reTry = findHashTargetElement(location.hash);
    if (reTry) {
      reTry.scrollIntoView({
        block: 'start',
        behavior: 'smooth',
      });
    }
  }, 300);
};

const handleLocationChanged = (dispatch, location, routeConfiguration, delayed, scrollOptions) => {
  const nextDelayed = setPageScrollPosition(location, delayed, scrollOptions);
  const path = canonicalRoutePath(routeConfiguration, location);
  dispatch(locationChanged(location, path));
  return nextDelayed;
};

/**
 * RouteComponentRenderer handles loadData calls on client-side.
 * It also checks authentication and redirects unauthenticated users
 * away from routes that are for authenticated users only
 * (aka "auth: true" is set in routeConfiguration.js)
 *
 * This component is a container: it needs to be connected to Redux.
 */
export class RouteComponentRenderer extends Component {
  componentDidMount() {
    const { dispatch, location, routeConfiguration } = this.props;
    this.delayed = null;
    // Capture aff_* on first paint (before SPA navigations drop query params).
    captureAffiliateAttributionFromLocation(location);
    // Calling loadData on initial rendering (on client side).
    callLoadData(this.props);
    this.delayed = handleLocationChanged(dispatch, location, routeConfiguration, this.delayed);
  }

  componentDidUpdate(prevProps) {
    const { dispatch, location, routeConfiguration } = this.props;
    // Call for handleLocationChanged affects store/state
    // and it generates an unnecessary update.
    if (prevProps.location !== this.props.location) {
      captureAffiliateAttributionFromLocation(location);
      const samePage = isSamePathAndSearch(prevProps.location, location);
      const preserveScroll =
        prevProps.location.pathname === location.pathname && location.state?.preserveScroll === true;
      // Hash / history-key-only updates must not refetch the page. Seatics' mobile
      // quantity drawer uses pushState/hash so Back can close it; loadData here
      // looks like a listing reload and closes the drawer on the first tap.
      if (!samePage) {
        callLoadData(this.props);
      }
      this.delayed = handleLocationChanged(dispatch, location, routeConfiguration, this.delayed, {
        samePage,
        preserveScroll,
      });
    }
  }

  componentWillUnmount() {
    if (this.delayed) {
      window.clearTimeout(this.delayed);
    }
  }

  render() {
    const { route, match, location, staticContext } = this.props;
    const { component: RouteComponent, authPage = 'SignupPage', extraProps } = route;
    const canShow = canShowComponent(this.props);
    if (!canShow) {
      staticContext.unauthorized = true;
    }
    return canShow ? (
      <LoadableComponentErrorBoundary>
        <RouteComponent params={match.params} location={location} {...extraProps} />
      </LoadableComponentErrorBoundary>
    ) : (
      <NamedRedirect
        name={authPage}
        state={{ from: `${location.pathname}${location.search}${location.hash}` }}
      />
    );
  }
}

RouteComponentRenderer.defaultProps = { staticContext: {} };

RouteComponentRenderer.propTypes = {
  isAuthenticated: bool.isRequired,
  logoutInProgress: bool.isRequired,
  route: propTypes.route.isRequired,
  routeConfiguration: arrayOf(propTypes.route).isRequired,
  match: shape({
    params: object.isRequired,
    url: string.isRequired,
  }).isRequired,
  location: shape({
    search: string.isRequired,
  }).isRequired,
  staticContext: object,
  dispatch: func.isRequired,
};

const mapStateToProps = state => {
  const { isAuthenticated, logoutInProgress } = state.auth;
  return { isAuthenticated, logoutInProgress };
};
const RouteComponentContainer = compose(connect(mapStateToProps))(RouteComponentRenderer);

/**
 * Routes component creates React Router rendering setup.
 * It needs routeConfiguration (named as "routes") through props.
 * Using that configuration it creates navigation on top of page-level
 * components. Essentially, it's something like:
 * <Switch>
 *   <Route render={pageA} />
 *   <Route render={pageB} />
 * </Switch>
 */
const Routes = (props, context) => {
  const routeConfiguration = useRouteConfiguration();
  const config = useConfiguration();
  const { isAuthenticated, logoutInProgress, logLoadDataCalls } = props;

  const toRouteComponent = route => {
    const renderProps = {
      isAuthenticated,
      logoutInProgress,
      route,
      routeConfiguration,
      config,
      logLoadDataCalls,
    };

    // By default, our routes are exact.
    // https://reacttraining.com/react-router/web/api/Route/exact-bool
    const isExact = route.exact != null ? route.exact : true;
    return (
      <Route
        key={route.name}
        path={route.path}
        exact={isExact}
        render={matchProps => (
          <RouteComponentContainer
            {...renderProps}
            match={matchProps.match}
            location={matchProps.location}
            staticContext={matchProps.staticContext}
          />
        )}
      />
    );
  };

  return (
    <Switch>
      {routeConfiguration.map(toRouteComponent)}
      <Route component={NotFoundPage} />
    </Switch>
  );
};

export default withRouter(Routes);
